import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Gift, Package, Wallet, LogOut, ArrowRight, ShoppingBag, MapPin, Truck, ExternalLink, ChevronDown, ChevronUp, Sparkles, Mail, AlertCircle, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { formatPrice } from "../lib/format";
import OrderStepper from "../components/OrderStepper";

const STATUS_META = {
  pending: { label: "En attente de paiement", className: "bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30" },
  paid: { label: "Payée", className: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  shipped: { label: "Expédiée", className: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  delivered: { label: "Livrée", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  cancelled: { label: "Annulée", className: "bg-red-500/10 text-red-300 border-red-500/30" },
};

export default function Account() {
  const { user, logout } = useAuth();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/orders/me").then((r) => r.data).catch(() => []),
      api.get("/referral/me").then((r) => r.data).catch(() => null),
      api.get("/loyalty/me").then((r) => r.data).catch(() => null),
    ]).then(([o, ref, lo]) => {
      if (cancelled) return;
      setOrders(o || []);
      setReferral(ref);
      setLoyalty(lo);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => {
    const paidOrders = orders.filter((o) => ["paid", "shipped", "delivered"].includes(o.status));
    const totalSpent = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
    return {
      ordersCount: orders.length,
      paidCount: paidOrders.length,
      totalSpent,
      referralCredit: referral?.credit_balance_eur || 0,
      referralCode: referral?.referral_code || null,
      wishlistCount,
      loyaltyPoints: loyalty?.points || 0,
      loyaltyRedeemable: loyalty?.redeemable_eur || 0,
    };
  }, [orders, referral, wishlistCount, loyalty]);

  const firstName = user?.name?.split(" ")[0] || user?.name || "";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="bg-[#001F3F] text-white" data-testid="account-page">
      {/* Email verification banner */}
      {user && user.email_verified === false && (
        <EmailVerificationBanner />
      )}

      {/* ─── HERO ─── */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">Espace client</div>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tighter text-white" data-testid="account-greeting">
                Bonjour, {firstName}
              </h1>
              <p className="mt-3 text-sm text-white/60">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white/60 hover:bg-white/5 transition-all uppercase tracking-[0.25em] text-xs font-bold px-5 py-3"
              data-testid="account-logout-btn"
            >
              <LogOut size={14} strokeWidth={2} />
              Se déconnecter
            </button>
          </div>
        </div>
      </section>

      {/* ─── KPI CARDS ─── */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              icon={Package}
              label="Commandes"
              value={stats.ordersCount}
              hint={stats.paidCount > 0 ? `${stats.paidCount} payée${stats.paidCount > 1 ? "s" : ""}` : "Aucune payée"}
              testid="stat-orders"
            />
            <StatCard
              icon={Wallet}
              label="Total dépensé"
              value={formatPrice(stats.totalSpent)}
              hint="Sur commandes confirmées"
              testid="stat-spent"
            />
            <StatCard
              icon={Gift}
              label="Crédit parrainage"
              value={`${stats.referralCredit.toFixed(2)} €`}
              hint={stats.referralCode ? `Code · ${stats.referralCode}` : "Devenez parrain"}
              accent={stats.referralCredit > 0}
              testid="stat-credit"
            />
            <StatCard
              icon={Sparkles}
              label="Points fidélité"
              value={stats.loyaltyPoints}
              hint={
                stats.loyaltyRedeemable > 0
                  ? `≈ ${stats.loyaltyRedeemable.toFixed(2)} € disponibles`
                  : "1 € dépensé = 1 point"
              }
              accent={stats.loyaltyRedeemable > 0}
              testid="stat-loyalty"
            />
            <StatCard
              icon={Heart}
              label="Wishlist"
              value={stats.wishlistCount}
              hint={stats.wishlistCount > 0 ? "Articles sauvegardés" : "Liste vide"}
              testid="stat-wishlist"
            />
          </div>
        </div>
      </section>

      {/* ─── QUICK ACTIONS ─── */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <QuickAction
              to="/parrainage"
              icon={Gift}
              title="Programme parrainage"
              desc="Invitez vos amis, gagnez 15 € de crédit à chaque filleul."
              testid="quick-referral"
            />
            <QuickAction
              to="/wishlist"
              icon={Heart}
              title="Ma wishlist"
              desc={stats.wishlistCount > 0 ? `${stats.wishlistCount} article${stats.wishlistCount > 1 ? "s" : ""} sauvegardé${stats.wishlistCount > 1 ? "s" : ""}.` : "Aucun article pour l'instant."}
              testid="quick-wishlist"
            />
            <QuickAction
              to="/shop"
              icon={ShoppingBag}
              title="Continuer mes achats"
              desc="Découvrez les nouveautés Smart Tech & Wellness."
              testid="quick-shop"
            />
          </div>
        </div>
      </section>

      {/* ─── ORDERS ─── */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">Historique</div>
              <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tighter text-white">
                Mes commandes
              </h2>
            </div>
            <Link
              to="/tracking"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white underline-offset-4 hover:underline"
              data-testid="account-tracking-link"
            >
              <Truck size={14} /> Suivre une commande
            </Link>
          </div>

          {loading ? (
            <div className="space-y-4" data-testid="orders-loading">
              {[1, 2].map((i) => (
                <div key={i} className="border border-white/10 bg-[#0A2A4F] h-32 animate-pulse" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="border border-white/10 bg-[#0A2A4F] p-12 text-center" data-testid="orders-empty">
              <Package size={36} strokeWidth={1.25} className="mx-auto text-white/30" />
              <p className="mt-4 text-sm text-white/60">Vous n'avez pas encore passé de commande.</p>
              <Link
                to="/shop"
                className="mt-6 inline-flex items-center gap-2 bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
                data-testid="orders-empty-cta"
              >
                Découvrir la boutique <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => {
                const meta = STATUS_META[o.status] || { label: o.status, className: "bg-white/10 text-white border-white/20" };
                const isOpen = expandedOrder === o.id;
                return (
                  <div
                    key={o.id}
                    className="border border-white/10 bg-[#0A2A4F] transition-colors hover:border-white/25"
                    data-testid={`order-${o.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedOrder(isOpen ? null : o.id)}
                      className="w-full text-left px-6 py-5 flex flex-wrap items-center justify-between gap-4"
                      data-testid={`order-toggle-${o.id}`}
                    >
                      <div className="flex-1 min-w-[180px]">
                        <div className="text-xs font-bold uppercase tracking-widest text-[#C5CDD8]">
                          Commande #{o.id.slice(0, 8).toUpperCase()}
                        </div>
                        <div className="text-xs text-white/55 mt-1">
                          {new Date(o.created_at).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })}
                          {" · "}
                          {o.items.length} article{o.items.length > 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 border ${meta.className}`}>
                        {meta.label}
                      </div>
                      <div className="font-display text-xl font-bold text-white">
                        {formatPrice(o.total)}
                      </div>
                      <div className="text-white/60">
                        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-6 border-t border-white/10" data-testid={`order-details-${o.id}`}>
                        <OrderStepper status={o.status} paymentStatus={o.payment_status} />

                        <div className="mt-5 space-y-3">
                          {o.items.map((it, i) => (
                            <div key={i} className="flex items-center gap-4 text-sm">
                              <div className="w-14 h-14 bg-[#001F3F] flex-shrink-0 border border-white/5 overflow-hidden">
                                {it.image && (
                                  <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                                )}
                              </div>
                              <div className="flex-1 text-white">{it.name}</div>
                              <div className="text-white/55">× {it.quantity}</div>
                              <div className="font-bold text-white whitespace-nowrap">
                                {formatPrice(it.price * it.quantity)}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Pricing breakdown */}
                        <div className="mt-6 pt-4 border-t border-white/10 text-sm space-y-1.5">
                          <Row label="Sous-total" value={formatPrice(o.subtotal)} />
                          {o.discount > 0 && (
                            <Row label={`Code promo${o.coupon_code ? ` · ${o.coupon_code}` : ""}`} value={`− ${formatPrice(o.discount)}`} accent />
                          )}
                          {o.bundle_discount > 0 && (
                            <Row label={o.bundle_label || "Remise pack"} value={`− ${formatPrice(o.bundle_discount)}`} accent />
                          )}
                          <Row label="Livraison" value={o.shipping > 0 ? formatPrice(o.shipping) : "Offerte"} />
                          <div className="flex justify-between pt-2 mt-2 border-t border-white/10 text-base">
                            <span className="font-bold uppercase tracking-widest text-xs text-white/80 self-center">Total</span>
                            <span className="font-display text-lg font-bold text-white">{formatPrice(o.total)}</span>
                          </div>
                        </div>

                        {/* Shipping address */}
                        {o.shipping_address && (
                          <div className="mt-6 grid sm:grid-cols-2 gap-6">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50 mb-2 flex items-center gap-2">
                                <MapPin size={12} /> Adresse de livraison
                              </div>
                              <div className="text-sm text-white/80 leading-relaxed">
                                {o.shipping_address.address}
                                <br />
                                {o.shipping_address.postal_code} {o.shipping_address.city}
                                <br />
                                {o.shipping_address.country}
                              </div>
                            </div>
                            <div className="flex sm:justify-end items-start">
                              <Link
                                to="/tracking"
                                className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-5 py-3 self-start"
                                data-testid={`order-track-${o.id}`}
                              >
                                <Truck size={14} /> Suivre ma commande
                                <ExternalLink size={12} />
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ─── ACCOUNT INFO + SUPPORT ─── */}
      <section className="border-t border-white/10 bg-[#000B1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid lg:grid-cols-2 gap-10">
          <div data-testid="account-info-block">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">Mes informations</div>
            <h3 className="font-display mt-2 text-2xl font-bold uppercase tracking-tighter">Profil</h3>
            <div className="mt-6 space-y-4 text-sm">
              <InfoRow label="Nom" value={user?.name || "—"} />
              <InfoRow label="Email" value={user?.email || "—"} />
              <InfoRow label="Type de compte" value={user?.role === "admin" ? "Administrateur" : "Client"} />
            </div>
            <p className="mt-6 text-xs text-white/45">
              Pour modifier vos informations, contactez-nous à <a href="mailto:contact@nordstellatech.fr" className="underline hover:text-white">contact@nordstellatech.fr</a>.
            </p>
          </div>

          <div data-testid="account-support-block">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">Besoin d'aide ?</div>
            <h3 className="font-display mt-2 text-2xl font-bold uppercase tracking-tighter">Support &amp; ressources</h3>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <SupportLink to="/tracking" label="Suivi de commande" />
              <SupportLink to="/shipping" label="Livraison &amp; retours" />
              <SupportLink to="/contact" label="Nous contacter" />
              <SupportLink to="/legal/cgv" label="CGV" />
              <SupportLink to="/legal/privacy" label="Confidentialité" />
              <SupportLink to="/legal/cookies" label="Cookies" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <div
      className={`border ${accent ? "border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 to-transparent" : "border-white/10 bg-white/[0.03]"} p-5`}
      data-testid={testid}
    >
      <Icon size={18} strokeWidth={1.5} className={accent ? "text-[#FFD700]" : "text-[#C5CDD8]"} />
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55 mt-3">{label}</div>
      <div className="font-display text-3xl font-bold tracking-tight text-white mt-1.5">{value}</div>
      <div className="text-xs text-white/40 mt-1 truncate">{hint}</div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      className="group border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/25 p-6 transition-all flex flex-col justify-between min-h-[140px]"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <Icon size={22} strokeWidth={1.5} className="text-[#FFD700]" />
        <ArrowRight size={16} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
      </div>
      <div className="mt-5">
        <div className="font-display text-lg font-bold uppercase tracking-tight">{title}</div>
        <div className="text-xs text-white/55 mt-1.5 leading-relaxed">{desc}</div>
      </div>
    </Link>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-white/55">{label}</span>
      <span className={accent ? "text-[#FFD700] font-bold" : "text-white"}>{value}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-white/10">
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}

function SupportLink({ to, label }) {
  return (
    <Link
      to={to}
      className="border border-white/10 hover:border-white/30 hover:bg-white/5 transition-all px-4 py-3 text-xs font-bold uppercase tracking-widest text-white/75 hover:text-white flex items-center justify-between"
    >
      <span>{label}</span>
      <ArrowRight size={12} />
    </Link>
  );
}

function EmailVerificationBanner() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const handleResend = async () => {
    setSending(true);
    try {
      await api.post("/auth/verify-email/resend");
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };
  return (
    <div
      className="border-b border-[#FFD700]/30 bg-[#FFD700]/10 text-white"
      data-testid="email-verification-banner"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div className="flex items-center gap-2.5 flex-1 min-w-[260px]">
          <AlertCircle size={16} className="text-[#FFD700] flex-shrink-0" />
          <span>
            <strong className="text-white">Email non vérifié.</strong>{" "}
            <span className="text-white/75">Confirmez votre adresse pour pleinement activer votre compte.</span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleResend}
          disabled={sending || sent}
          className="inline-flex items-center gap-2 border border-white/50 hover:bg-white/10 transition-all uppercase tracking-[0.2em] text-[10px] font-bold px-4 py-2 disabled:opacity-50"
          data-testid="resend-verification-btn"
        >
          {sent ? (
            <><Check size={12} /> Lien envoyé</>
          ) : (
            <><Mail size={12} /> {sending ? "Envoi…" : "Renvoyer le lien"}</>
          )}
        </button>
      </div>
    </div>
  );
}

